import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Link, Typography, styled } from "@mui/material";

import { UseFormReturn } from "react-hook-form";
import { CIDocFormData } from "@interfaces";
import {
  BottomActionArea,
  BottomActionButton,
  Modal,
  SingleFormInput,
  NavigateBeforeButton,
  FormContentContainer,
  FormHeader,
  FormBody,
  StyledAlert,
} from "@components";
import { FormContainer } from "@styles/styled";
import { useMutation } from "@tanstack/react-query";
import { apiGateway } from "@contexts/index";
import { ErrorType } from "../hooks/onQuestionChallengeError";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";
import { AxiosError } from "axios";

const StyledLink = styled(Link)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette.text.link,
  cursor: "pointer",
  padding: "8px 0px",
}));

const ModalChildrenRoot = styled(Box)({
  marginTop: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  alignItems: "center",
});

const CIImgGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

const CIImgTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
}));

// TODO: if we don't want to set this values with px, we should take
// CIImg and its zoom as two different images with position absolute
const CIImg = styled("img")({
  width: "83.765%",
  marginLeft: "0.75rem",
});

const OldCIImg = styled("img")({
  width: "80%",
});
interface Props {
  docNumErrorDescription: string | React.ReactNode | null;
  form: UseFormReturn<CIDocFormData>;
}
export const CIDocForm: React.FC<Props> = ({
  docNumErrorDescription,
  form,
}) => {
  const { getEnrolmentStatus, setCurrentStep, setCurrentStepStatus } =
    useEnrolmentStatusContext();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = form;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCIDocModalVisible, setIsCIDocModalVisible] = useState(false);
  const [ciImgsSrcs, setCiImgsSrcs] = useState({ currentCi: "", oldCi: "" });

  const handleBack = useCallback(() => {
    const requestId = searchParams.get("requestId");
    const goBackUrl = requestId
      ? `/app/solicitudes/${requestId}/receta/crear`
      : "/app/solicitudes";
    navigate(goBackUrl);
  }, [navigate, searchParams]);

  const { mutate: saveCiDocNumber, isPending: isSavingCiDocNumber } =
    useMutation({
      mutationFn: async (data: CIDocFormData) => {
        await apiGateway<void, ErrorType>({
          method: "post",
          url: "/enrolment/ci-doc-num",
          data: data,
        });
      },
      onSuccess: async () => {
        await getEnrolmentStatus();
      },
      onError: async (error: AxiosError<ErrorType>) => {
        console.error("Error saving ci doc number: ", error);
        setError("ciDocNum", {
          type: "manual",
          message: "No corresponde a un N° de documento",
        });
        const errorData = error.response?.data;
        const nextStep = errorData?.nextStep;
        const nextStepStatus = errorData?.nextStepStatus;
        if (nextStep && nextStepStatus) {
          setCurrentStep(nextStep);
          setCurrentStepStatus(nextStepStatus);
        } else {
          await getEnrolmentStatus();
        }
      },
    });
  const onSubmit = useCallback(
    (data: CIDocFormData) => {
      if (!data.ciDocNum) {
        return;
      }

      const validationChallengePayload: CIDocFormData = {
        ciDocNum: data.ciDocNum.replaceAll(".", ""),
      };
      saveCiDocNumber(validationChallengePayload);
    },
    [saveCiDocNumber],
  );

  useEffect(() => {
    const ciImg = new Image();
    const oldCiImg = new Image();
    ciImg.src = "/assets/images/ci-doc.png";
    oldCiImg.src = "/assets/images/ci-serie-num.png";
    ciImg.onload = () => {
      setCiImgsSrcs((prevState) => ({ ...prevState, currentCi: ciImg.src }));
    };
    oldCiImg.onload = () => {
      setCiImgsSrcs((prevState) => ({ ...prevState, oldCi: oldCiImg.src }));
    };
  }, []);

  return (
    <>
      <FormHeader
        title="Verificación de identidad"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Typography>
            {"Ingresa el número de serie o documento de tu cédula de identidad"}
          </Typography>
          <FormContainer onSubmit={handleSubmit((data) => onSubmit(data))}>
            <SingleFormInput
              title="N° Serie/Documento"
              parameterName="ciDocNum"
              placeholder=""
              errorText="Debes ingresar tu número de documento."
              register={register}
              errors={errors}
              obscureInTracker
              validate={(value: string) => {
                const cleanValue = value.replaceAll(".", "");
                return (
                  cleanValue.length >= 7 ||
                  "No corresponde a un N° de documento"
                );
              }}
            />

            <StyledLink
              onClick={(event) => {
                event.preventDefault();
                setIsCIDocModalVisible(true);
              }}
            >
              ¿Donde encuentro el nº de serie/documento?
            </StyledLink>

            {docNumErrorDescription ? (
              <StyledAlert severity="error">
                {docNumErrorDescription}
              </StyledAlert>
            ) : null}

            <BottomActionArea>
              <BottomActionButton type="submit" disabled={isSavingCiDocNumber}>
                {isSavingCiDocNumber ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Siguiente"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
        </FormBody>
      </FormContentContainer>
      <Modal
        title=""
        onClose={() => {
          setIsCIDocModalVisible(false);
        }}
        isOpen={isCIDocModalVisible}
      >
        <ModalChildrenRoot>
          <CIImgGroup>
            <CIImgTitle>Cédula identidad actual</CIImgTitle>
            {ciImgsSrcs.currentCi ? (
              <CIImg src={ciImgsSrcs.currentCi} alt="Cédula identidad actual" />
            ) : (
              <Typography>Cargando...</Typography>
            )}
          </CIImgGroup>
          <CIImgGroup>
            <CIImgTitle>Cédula identidad antigua</CIImgTitle>
            {ciImgsSrcs.oldCi ? (
              <OldCIImg src={ciImgsSrcs.oldCi} alt="Cédula identidad antigua" />
            ) : (
              <Typography>Cargando...</Typography>
            )}
          </CIImgGroup>
        </ModalChildrenRoot>
      </Modal>
    </>
  );
};
